import type { Checker } from "./mapcheck";
import { RIDES, rideTricks, type Ride, type RideTrickTag, type RideTricks } from "./sim/rides";
import { MAX_TAGS } from "./graffiti";
import { MAX_TARGETS } from "./targets";
import {
  FLIP_TRICKS,
  GRAB_TRICKS,
  GRIND_TRICKS,
  LIP_TRICKS,
  MANUAL_HOLD_TRICKS,
  MANUAL_POP_TRICKS,
  MANUAL_TRICKS,
  POP_VARIANTS,
  SPECIAL_TRICKS,
} from "./tricks";

// Challenge kinds beyond the goal list (#66): a map file's `challenges`
// entry with a `kind` is a task, not one of the `goals` block's score, gap
// and collect goals. Each kind is one entry in `TASK_KINDS`: its fields, its
// map-file checks, its goal text and a checker over each landed combo. They
// only run as free-skate challenges (the 2-minute run keeps the goal list).
// Sim-free: a checker reads the `landed` event's combo text, plus the
// longest manual and grind in that combo, which `Run` times from `Riding`.
// `targets` (#137) isn't judged on a combo: `Run` counts the paintball
// targets burst on the challenge's clock and asks `hits`; `graffiti` (#151)
// the same with the tags washed clean.

export type TaskKind = "trick" | "special" | "comboScore" | "comboTricks" | "manual" | "grind" | "targets" | "graffiti";

/** A task, from a map file's `challenges` entry with a `kind` (see `taskOf`). */
export interface TaskGoal {
  id: string;
  kind: TaskKind;
  /** trick: the trick to land ("Kickflip", "Wallride"); any stance, pop or spin in front of it counts ("Switch FS 360 Kickflip"). */
  trick?: string;
  /** trick: the least spin it needs, in degrees (180, 360, 540...). */
  spin?: number;
  /** trick: a gap (from the map's `gaps`) to land in the same combo. */
  gap?: string;
  /** comboScore: the least a landed combo must be worth. */
  points?: number;
  /** comboTricks: the least tricks (gaps count) in one landed combo. */
  tricks?: number;
  /** manual / grind: the least seconds of one unbroken manual or grind, in a combo that lands. */
  seconds?: number;
  /** targets / graffiti: how many of the map's paintball targets to burst, or graffiti tags to wash off (each counts once). */
  count?: number;
}

/** What the skater is riding this step, for the timed kinds. */
export interface Riding {
  manual: boolean;
  grind: boolean;
}

/** A landed combo, as a checker sees it. */
export interface LandedCombo {
  /** Its tricks and gaps, in order ("Switch Kickflip", "Funbox Transfer"...). */
  entries: readonly string[];
  /** What it scored. */
  points: number;
  /** Its longest unbroken manual and grind, in seconds (0 if none, or not timed). */
  manual?: number;
  grind?: number;
}

/** Which kind of challenge marker and card a task gets (see `ChallengeKind`). */
export type TaskMarker = "trick" | "combo" | "targets" | "graffiti";

interface TaskKindDef {
  /** Map-file fields on top of the ladder's (`id`, `tier`, `requires`, `statPoints`, `kind`). */
  fields: readonly string[];
  marker: TaskMarker;
  /** Checks an entry's own fields against the map. */
  check(c: Checker, o: Record<string, unknown>, at: string, map: TaskMap): void;
  text(t: TaskGoal): string;
  /** Whether this landed combo does it. */
  done(t: TaskGoal, combo: LandedCombo): boolean;
  /** Whether this many different targets burst (`targets`) or tags washed off (`graffiti`) on the challenge's clock does it. */
  hits?(t: TaskGoal, n: number): boolean;
  /** The event `hits` counts, one per target or tag (its `index`). */
  counts?: "target" | "clean";
}

/** What a task's map-file checks know of its map. */
export interface TaskMap {
  /** The map's gap names. */
  gaps: readonly string[];
  /** How many paintball targets it has (#137). */
  targets: number;
  /** How many graffiti tags it has (#151). */
  tags: number;
}

/** Tricks the sim names outside the trick tables. */
const OTHER_TRICKS = ["Ollie", "Spine Transfer", "Transfer", "Acid Drop", "Wallride", "Wallplant", "Wallie", "Revert"];

/** A ride's tricks with a tag (`sim/rides.ts`' one walk over its tables), by name. */
function rideNamesTagged(t: RideTricks, tag: RideTrickTag): string[] {
  return rideTricks(t, tag).map((x) => x.name);
}

/** A ride's tricks that start an air (a spin can be credited to them), and those that start somewhere else. */
function rideAirNames(t: RideTricks): string[] {
  return rideNamesTagged(t, "air");
}
function rideGroundNames(t: RideTricks): string[] {
  return rideNamesTagged(t, "ground");
}

/** Every trick a ride's tables name. */
function rideNames(t: RideTricks): string[] {
  return [...rideAirNames(t), ...rideGroundNames(t), ...rideNamesTagged(t, "pop")];
}

const RIDE_LIST: readonly Ride[] = Object.values(RIDES);

/**
 * Every trick name a `trick` task may ask for: the board's and every ride's
 * (#138: a Tailwhip, an X-Up Manual). Names built as they're pulled (a
 * Superman Backflip, a Backflip Tailwhip) aren't in a table, so they aren't here.
 */
export const TRICK_NAMES: ReadonlySet<string> = new Set([
  ...[FLIP_TRICKS, GRAB_TRICKS, GRIND_TRICKS, LIP_TRICKS, MANUAL_POP_TRICKS, MANUAL_HOLD_TRICKS].flatMap((t) => Object.values(t).map((x) => x.name)),
  ...Object.values(MANUAL_TRICKS).map((x) => x.name),
  ...POP_VARIANTS.map((x) => x.name),
  ...SPECIAL_TRICKS.map((x) => x.name),
  ...OTHER_TRICKS,
  ...RIDE_LIST.flatMap((r) => rideNames(r.tricks)),
]);

/**
 * Tricks a spin can't be credited to: they start on a rail, a ramp's lip,
 * the ground or a wall, so the air's spin scores before them as its own
 * entry ("FS 180 Ollie + Boardslide"). Names an air can also start with
 * (a Kickflip is a flip and a manual's flatground flip) aren't in it.
 */
const UNSPUN_TRICKS: ReadonlySet<string> = (() => {
  const air = new Set([...POP_VARIANTS.map((x) => x.name), ...RIDE_LIST.flatMap((r) => rideAirNames(r.tricks))]);
  return new Set([...RIDE_LIST.flatMap((r) => rideGroundNames(r.tricks)), "Wallride", "Revert"].filter((name) => !air.has(name)));
})();

/** The ride a trick is only on, if the board doesn't do it (#138): a task for it says so. */
function onlyOnRide(name: string): Ride | null {
  const has = (r: Ride) => rideNames(r.tricks).includes(name);
  if (has(RIDES.board) || POP_VARIANTS.some((x) => x.name === name) || OTHER_TRICKS.includes(name)) return null;
  return RIDE_LIST.find(has) ?? null;
}

/** Every ride's specials (#138): a Flair on the bike lands the special task too. */
const SPECIAL_NAMES: readonly string[] = RIDE_LIST.flatMap((r) => r.tricks.specials.map((x) => x.name));

/**
 * A special, or a special with a whip or grab added mid-flip on the bike
 * (#138: "Flair Tailwhip", "Superman Double Backflip"), still one special.
 */
export function isSpecialTrick(trick: string): boolean {
  return SPECIAL_NAMES.some((n) => trick === n || trick.startsWith(`${n} `) || trick.endsWith(` ${n}`));
}

/** Most points or tricks a combo task may ask for. */
const MAX_COMBO_POINTS = 1_000_000;
const MAX_COMBO_TRICKS = 20;
/** Longest a timed task may ask for, and the step its seconds come in. */
const MAX_TIMED_SECONDS = 30;
const TIMED_STEP = 0.5;

/** A timed kind: one unbroken manual or grind of at least `seconds`, in a combo that lands. */
function timedKind(what: "manual" | "grind", verb: string): TaskKindDef {
  return {
    fields: ["seconds"],
    marker: "combo",
    check(c, o, at) {
      const n = c.num(o, "seconds", at, { min: TIMED_STEP, max: MAX_TIMED_SECONDS });
      if (n % TIMED_STEP !== 0) c.fail(`${at}.seconds`, `expected a multiple of ${TIMED_STEP}`);
    },
    text: (t) => `${verb} for ${t.seconds} second${t.seconds === 1 ? "" : "s"}`,
    done: (t, combo) => (combo[what] ?? 0) >= t.seconds!,
  };
}

/**
 * A combo entry split into what's in front of the trick and the trick:
 * "Switch Nollie FS 360 Kickflip" is 360 degrees of spin and "Kickflip". A
 * spin on its own ("Nollie BS 180") has an empty trick. Anything else in
 * front ("Double Kickflip") stays part of the trick's name.
 */
export function entryTrick(entry: string): { trick: string; spin: number } {
  const m = /^(?:Switch )?(?:(?:Nollie|Fakie) ?)?(?:(?:FS|BS) (\d+) ?)?(.*)$/.exec(entry)!;
  return { trick: m[2]!, spin: m[1] ? Number(m[1]) : 0 };
}

/** A landed event's combo text, split into its entries. */
export function comboEntries(text: string): string[] {
  return text ? text.split(" + ") : [];
}

export const TASK_KINDS: Record<TaskKind, TaskKindDef> = {
  trick: {
    fields: ["trick", "spin", "gap"],
    marker: "trick",
    check(c, o, at, { gaps }) {
      if (o.trick === undefined && o.spin === undefined) c.fail(at, "needs a trick, a spin or both");
      if (o.trick !== undefined) {
        const name = c.str(o, "trick", at);
        if (name && !TRICK_NAMES.has(name)) c.fail(`${at}.trick`, `"${name}" isn't a trick the game knows`);
      }
      if (o.spin !== undefined) {
        const spin = c.num(o, "spin", at, { min: 180, max: 1080 });
        if (spin % 180 !== 0) c.fail(`${at}.spin`, "expected a multiple of 180");
        if (typeof o.trick === "string" && UNSPUN_TRICKS.has(o.trick))
          c.fail(`${at}.spin`, `"${o.trick}" can't be spun into (the spin scores as its own air), so drop the spin`);
      }
      if (o.gap !== undefined) {
        const gap = c.str(o, "gap", at);
        if (gap && !gaps.includes(gap)) c.fail(`${at}.gap`, `"${gap}" isn't one of this map's gaps`);
      }
    },
    text(t) {
      const what = [t.spin ? `${t.spin}` : "", t.trick ?? (t.spin ? "spin" : "")].filter(Boolean).join(" ");
      const ride = t.trick === undefined ? null : onlyOnRide(t.trick);
      const on = ride ? ` on the ${ride.name}` : "";
      return t.gap ? `${what} across the ${t.gap} gap${on}` : `Land a ${what}${on}`;
    },
    done(t, combo) {
      const hit = combo.entries.some((e) => {
        const { trick, spin } = entryTrick(e);
        return (t.trick === undefined || trick === t.trick) && spin >= (t.spin ?? 0);
      });
      return hit && (t.gap === undefined || combo.entries.includes(t.gap));
    },
  },
  special: {
    fields: [],
    marker: "trick",
    check() {},
    text: () => "Land a special trick",
    done: (_, combo) => combo.entries.some((e) => isSpecialTrick(entryTrick(e).trick)),
  },
  comboScore: {
    fields: ["points"],
    marker: "combo",
    check(c, o, at) {
      const n = c.num(o, "points", at, { min: 1, max: MAX_COMBO_POINTS });
      if (!Number.isInteger(n)) c.fail(`${at}.points`, "expected a whole number");
    },
    text: (t) => `Land a ${t.points!.toLocaleString("en-US")} point combo`,
    done: (t, combo) => combo.points >= t.points!,
  },
  comboTricks: {
    fields: ["tricks"],
    marker: "combo",
    check(c, o, at) {
      const n = c.num(o, "tricks", at, { min: 2, max: MAX_COMBO_TRICKS });
      if (!Number.isInteger(n)) c.fail(`${at}.tricks`, "expected a whole number");
    },
    text: (t) => `Land a ${t.tricks}-trick combo`,
    done: (t, combo) => combo.entries.length >= t.tricks!,
  },
  manual: timedKind("manual", "Manual"),
  grind: timedKind("grind", "Grind"),
  targets: {
    fields: ["count"],
    marker: "targets",
    check(c, o, at, map) {
      const n = c.num(o, "count", at, { min: 1, max: MAX_TARGETS });
      if (!Number.isInteger(n)) c.fail(`${at}.count`, "expected a whole number");
      else if (n > map.targets) c.fail(`${at}.count`, `the map only has ${map.targets} target${map.targets === 1 ? "" : "s"}`);
    },
    text: (t) => `Hit ${t.count} target${t.count === 1 ? "" : "s"}`,
    done: () => false,
    hits: (t, burst) => burst >= t.count!,
    counts: "target",
  },
  graffiti: {
    fields: ["count"],
    marker: "graffiti",
    check(c, o, at, map) {
      const n = c.num(o, "count", at, { min: 1, max: MAX_TAGS });
      if (!Number.isInteger(n)) c.fail(`${at}.count`, "expected a whole number");
      else if (n > map.tags) c.fail(`${at}.count`, `the map only has ${map.tags} graffiti tag${map.tags === 1 ? "" : "s"}`);
    },
    text: (t) => `Wash off ${t.count} graffiti tag${t.count === 1 ? "" : "s"}`,
    done: () => false,
    hits: (t, cleaned) => cleaned >= t.count!,
    counts: "clean",
  },
};

export const TASK_KIND_NAMES = Object.keys(TASK_KINDS) as TaskKind[];

/** Whether a `challenges` entry is a task (it has a `kind`) rather than a goal-list goal. */
export function isTask(entry: { kind?: unknown }): entry is { kind: TaskKind } {
  return entry.kind !== undefined;
}

/** The task a checked `challenges` entry with a `kind` describes, without its ladder fields. */
export function taskOf(entry: TaskGoal & Record<string, unknown>): TaskGoal {
  const t: TaskGoal = { id: entry.id, kind: entry.kind };
  for (const f of TASK_KINDS[entry.kind].fields) if (entry[f] !== undefined) (t as unknown as Record<string, unknown>)[f] = entry[f];
  return t;
}

/** Checks a `challenges` entry's `kind` and that kind's fields. */
export function checkTask(c: Checker, o: Record<string, unknown>, at: string, map: TaskMap): void {
  if (typeof o.kind !== "string" || !Object.hasOwn(TASK_KINDS, o.kind)) {
    c.fail(`${at}.kind`, `expected one of ${TASK_KIND_NAMES.join(", ")}`);
    return;
  }
  TASK_KINDS[o.kind as TaskKind].check(c, o, at, map);
}
